import { isAxiosError } from 'axios';
import { axiosClient } from 'hooks/utils';
import { saveSchoolWithVerification } from 'utils/schoolSave';
import { verifySchoolTimezone } from 'utils/schoolTimezone';

export type SaveSchoolInput = {
  mode: 'create' | 'edit';
  organizationType: 'high_school' | 'college';
  school: {
    schoolName: string;
    schoolTitle: string;
    nickname?: string;
    website?: string;
    ncesCode?: string;
    ipedsId?: string;
    private: boolean;
    status: 'live' | 'waitlist' | 'suspended';
    primaryColor: string;
    liteLevel?: 'L1' | 'L2' | 'L3';
    country: 'US' | 'CA';
    state: string;
    districtCode?: string | null;
    lat?: number;
    lng?: number;
    timezone: string;
    alphabeticLunchWaves?: boolean;
  };
  logo?: File;
};

const uploadLogo = async (file: File) => {
  const formData = new FormData();
  formData.set('file', file);
  try {
    const { data } = await axiosClient.post<{ id: string }>(
      'v2/media/school_logo',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    if (!data.id) throw new Error('Missing logo ID');
    return data.id;
  } catch {
    throw new Error('Could not upload the logo. Please try again.');
  }
};

/** One save pipeline for every create/edit entry point and organization type. */
export const saveSchool = async ({
  mode,
  organizationType,
  school,
  logo,
}: SaveSchoolInput) => {
  await verifySchoolTimezone(school);
  const logoId = logo ? await uploadLogo(logo) : undefined;
  const creating = mode === 'create';
  const college = organizationType === 'college';
  const common = {
    schoolTitle: school.schoolTitle,
    nickname: school.nickname,
    website: school.website ?? '',
    private: school.private,
    status: creating && college ? 'live' : school.status,
    primaryColor: school.primaryColor,
    country: school.country,
    state: school.state,
    lat: school.lat,
    lng: school.lng,
    timezone: school.timezone,
    ...(creating ? { domains: [], studentsCount: 0 } : {}),
  };
  const body = college
    ? {
        ...common,
        ...(creating
          ? { friendlyName: school.schoolName }
          : { schoolId: school.schoolName }),
        ipedsId: school.ipedsId,
        ...(logoId ? { mascotId: logoId } : {}),
      }
    : {
        ...common,
        schoolName: school.schoolName,
        ncesCode: school.ncesCode,
        districtCode: school.country === 'CA' ? null : school.districtCode,
        ...(!creating
          ? {
              liteLevel: school.liteLevel,
              alphabeticLunchWaves: school.alphabeticLunchWaves,
            }
          : {}),
        ...(logoId ? { logoId } : {}),
      };
  // College writes use different names for fields exposed by the shared GET.
  const verificationValues = college
    ? {
        ...common,
        schoolName: school.schoolName,
        ipedsId: school.ipedsId,
        ...(logoId ? { logoId } : {}),
      }
    : body;
  const { data } = await saveSchoolWithVerification(
    axiosClient,
    creating ? 'post' : college ? 'patch' : 'put',
    school.schoolName,
    body,
    {
      path: college ? 'v2/admin/colleges' : undefined,
      verificationValues,
    },
  );
  return data;
};

export const schoolSaveErrorMessage = (error: unknown): string => {
  if (isAxiosError<{ detail?: unknown }>(error)) {
    const detail = error.response?.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const messages = detail.flatMap((item: unknown) =>
        item &&
        typeof item === 'object' &&
        'msg' in item &&
        typeof item.msg === 'string'
          ? [item.msg]
          : [],
      );
      if (messages.length) return messages.join(', ');
    }
  }
  return error instanceof Error
    ? error.message
    : 'Could not save the school. Please try again.';
};
